import React, { useEffect } from "react";
import { getServerSideSplitTreatments } from "@leafly-com/split-next";
import Head from "next/head";
import { parse, ParsedQs, stringify } from "qs";
import { useSelector } from "react-redux";

import { FILTER_CONFIG_OPTIONS } from "api/requests/consumerApi/getMenuItemsV3";
import getProductsV3, { AvailableFilter } from "api/requests/getProductsV3";
import { getUserLocationData } from "api/requests/getUserLocation";
import serverConfig from "config/server";
import { Action, Category } from "constants/events";
import FulfillmentMode from "custom-types/FulfillmentMode";
import { MenuItem } from "custom-types/MenuItem";
import { Query } from "custom-types/Query";
import { ShopItem } from "custom-types/ShopItem";
import useDispensaries from "hooks/shop/useDispensaries";
import { useEventTracker } from "hooks/useEventTracker";
import useQueryParamState from "hooks/useQueryParamState";
import {
  getCoordinates,
  getIsValidStreetAddress,
} from "redux/selectors/location";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import {
  LEGACY_FILTER_MAP,
  normalizeShopFilterParam,
} from "utils/normalizeShopFilterParam";
import { getCountryCodeHeader } from "utils/requestContext";
import { getSplitKey } from "utils/split/getSplitKey";

import DeliveryMenu from "components/DeliveryGateway/DeliveryMenu";
import DeliveryMenuLoadingSkeletons from "components/DeliveryGateway/DeliveryMenu/DeliveryMenuLoadingSkeletons";
import DeliveryMenuCarousels from "components/DeliveryGateway/DeliveryMenuCarousels";
import DispensaryFulfillmentInfo from "components/DispensaryFulfillmentInfo";
import HeaderV2 from "components/HeaderV2";
import XIcon from "components/Icons/x.svg";
import { useFilterQueryParams } from "components/MenuFilters/v2/hooks";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";
import ShopNoResults from "components/ShopNoResults";
import UnifiedShop, {
  UNIFIED_SHOP_PAGE_SIZE,
} from "components/UnifiedShop/UnifiedShop";

export type ShopProps = {
  shopItems: ShopItem[];
  availableFilters: AvailableFilter[];
  totalCount: number;
  unifiedShopExperienceEnabled: boolean;
  debugAPIParams: string | null;
  env: string;
};

const ShopV1 = () => {
  const { publishEvent } = useEventTracker();

  const coordinates = useSelector(getCoordinates);
  const isValidAddress = useSelector(getIsValidStreetAddress);

  const [fulfillmentPreference, setFulfillmentPreference] = useQueryParamState(
    "fulfillmentPreference",
  );
  const [attemptedRetailerName, setAttemptedRetailerName] = useQueryParamState(
    "attemptedRetailerName",
  );

  const {
    appliedFilters,
    actions: { clearAllFilters },
  } = useFilterQueryParams();
  const filtersApplied = Object.keys(appliedFilters).length > 0;

  const [dispensarySort, setDispensarySort] =
    useQueryParamState("dispensarySort");

  const {
    deliveryDispensaries,
    pickupDispensaries,
    loading: loadingDispensaries,
  } = useDispensaries();

  useEffect(() => {
    if (isValidAddress && typeof window != "undefined") {
      window.scrollTo(0, 0);
    }
  }, [isValidAddress]);

  // * Determine which fulfillment mode to use before querying menu items
  let fulfillmentMode: FulfillmentMode;
  if (fulfillmentPreference) {
    fulfillmentMode = fulfillmentPreference as FulfillmentMode;
  } else if (deliveryDispensaries.length > 0) {
    fulfillmentMode = "delivery";
  } else {
    fulfillmentMode = "pickup";
  }

  const newFulfillmentMode =
    fulfillmentMode === "delivery" ? "pickup" : "delivery";

  // * Used for carousels, and to see if we have empty nearby results.
  const availableDispensaries =
    fulfillmentMode === "delivery" ? deliveryDispensaries : pickupDispensaries;

  return (
    <>
      <Head>
        <title>Shop Weed</title>
        <meta
          name="description"
          content="Find the best weed with Leafly. Order weed for same-day delivery, find reviews & menus from the top dispensaries."
        />
        <meta name="robots" content="noindex" />
      </Head>

      <HeaderV2 fulfillmentMode={fulfillmentMode} fullWidthBanner={true} />

      {!!attemptedRetailerName && (
        <div className="container rounded bg-leafly-white mt-xl p-md">
          <div className="flex justify-between">
            <div className="text-sm font-bold">{attemptedRetailerName}</div>
            <button onClick={() => setAttemptedRetailerName()}>
              <XIcon width="16" height="16" className="cursor-pointer" />
            </button>
          </div>
          <div className="text-xs font-bold text-error">
            Delivery unavailable to your address
          </div>
        </div>
      )}

      <>
        <DeliveryMenu
          coordinates={coordinates}
          filterConfig={FILTER_CONFIG_OPTIONS.SHOP_MENU}
          isDeliveryFulfillmentMode={fulfillmentMode === "delivery"}
          isPickupFulfillmentMode={fulfillmentMode === "pickup"}
          fulfillmentLoading={loadingDispensaries}
          fulfillmentModeUnavailable={!availableDispensaries.length}
          outerClassNames={"mt-xs md:mt-lg"}
          mobileClassName="mt-md"
          renderPreFilteredView={() => {
            if (loadingDispensaries) {
              return <DeliveryMenuLoadingSkeletons />;
            }

            return availableDispensaries.length > 0 ? (
              // * Render dispensary-centric view, without CategoryChips
              <DeliveryMenuCarousels
                dispensaries={availableDispensaries}
                isDeliveryAvailable={fulfillmentMode === "delivery"}
                selectedSort={dispensarySort}
                onChangeSort={setDispensarySort}
              />
            ) : (
              <div className="my-md">
                <ShopNoResults
                  action={() => setFulfillmentPreference(newFulfillmentMode)}
                  primaryLinkText={`shop ${newFulfillmentMode} instead`}
                  titleText={`${
                    fulfillmentMode === "delivery" ? "Delivery" : "Pickup"
                  } is not available at your address.`}
                  wrapperClassName="pb-md"
                />
              </div>
            );
          }}
          renderNoResults={() => {
            return !availableDispensaries.length ? (
              <ShopNoResults
                action={() => setFulfillmentPreference(newFulfillmentMode)}
                primaryLinkText={`shop ${newFulfillmentMode} instead`}
                titleText={`${
                  fulfillmentMode === "delivery" ? "Delivery" : "Pickup"
                } is not available at your address.`}
                wrapperClassName="pb-md"
              />
            ) : filtersApplied ? (
              <ShopNoResults
                action={() => clearAllFilters()}
                subtitleText="Try selecting fewer options or reset all filters"
                primaryLinkText="reset filters"
                titleText="No results found"
              />
            ) : (
              <ShopNoResults
                titleText="No matching results"
                primaryLinkText="shop all products"
                action={() => clearAllFilters()}
                wrapperClassName="pb-md"
              />
            );
          }}
          showStrainEffectCopy={true}
        />

        <RecentlyViewedMenuItems
          className="border-t border-light-grey pt-xxl mb-xxl"
          carouselClassName="container"
          onCardClick={(menuItem: MenuItem) =>
            publishEvent({
              action: "click",
              brandId: menuItem?.brand?.id,
              category: Category.deliveryGateFlow,
              dispensaryId: menuItem?.dispensaryId,
              label: "recently viewed menu item card",
              menuItemId: menuItem?.id,
              productId: menuItem?.product?.id,
            })
          }
          onCardImpression={(menuItem: MenuItem) =>
            publishEvent({
              action: Action.impression,
              brandId: menuItem?.brand?.id,
              category: Category.deliveryGateFlow,
              dispensaryId: menuItem.dispensaryId,
              label: "recently viewed menu item card",
              menuItemId: menuItem.id,
              nonInteractionEvent: true,
              productId: menuItem?.product?.id,
            })
          }
          menuItemProps={{
            renderFulfillmentInfo: (dispensary) =>
              fulfillmentMode === "delivery" && (
                <DispensaryFulfillmentInfo dispensary={dispensary} />
              ),
            showFulfillment: false,
          }}
        />
      </>
    </>
  );
};

const ShopPage: React.FC<ShopProps> = ({
  unifiedShopExperienceEnabled,
  debugAPIParams,
  env,
  ...newProps
}) => {
  useEffect(() => {
    const allowedEnvironments = ["staging", "integration", "local-dev"];
    if (allowedEnvironments.includes(env)) {
      getSplitKey().then((splitKey) => {
        /* eslint-disable no-console -- this is debugging we want in Integration in particular, this should eventually be cleaned up (https://leafly.atlassian.net/browse/SHH-1016) */
        console.log("Leafly internal dev logging:");
        console.log(`Env: ${env}`);
        console.log(`User Split Key: ${splitKey}`);
        console.log(`Products V3 Params: ${debugAPIParams}`);
        /* eslint-enable no-console */
      });
    }
  }, [debugAPIParams]);

  if (unifiedShopExperienceEnabled) {
    return <UnifiedShop {...newProps} />;
  }

  return <ShopV1 />;
};

export const getServerSideProps = createGetServerSideProps<ShopProps>(
  async (context) => {
    // TODO Clean up this experiment when complete (https://leafly.atlassian.net/browse/SHH-1046)
    const {
      "SHH-874_unifiedShoppingExperience_test": { treatment: usxTreatment },
    } = await getServerSideSplitTreatments(
      "SHH-874_unifiedShoppingExperience_test",
      getSplitKey(context),
    );

    const unifiedShopExperienceEnabled = usxTreatment === "on";

    // * This is a patch related to SHH-874_unifiedShoppingExperience_test.
    // * It alleviates issues when crawlers get inconsistent treatments for the experiment, and invoke the old UX using params from the new treatment.
    // * This is currently only an issue with the price/filterable_price params.
    // TODO This block should be removed when the SHH-874_unifiedShoppingExperience_test experiment is ended. (https://leafly.atlassian.net/browse/SHH-1046)
    if (!unifiedShopExperienceEnabled) {
      const [url, _hash] = context.resolvedUrl.split("#");
      const [path, query] = url.split("?");
      const { filter, ...others } = parse(query);
      const { price, ...otherFilters } = (filter as ParsedQs) || {};

      if (price) {
        const newQueryString = stringify(
          { filter: { filterable_price: price, ...otherFilters }, ...others },
          { arrayFormat: "brackets", indices: false },
        );
        return {
          redirect: {
            destination: `${path}${newQueryString ? `?${newQueryString}` : ""}`,
            statusCode: 302,
          },
        };
      }
    }

    let data;
    let debugAPIParams = null;
    if (unifiedShopExperienceEnabled) {
      const [url, _hash] = context.resolvedUrl.split("#");
      const [path, query] = url.split("?");
      const {
        filter,
        fulfillment,
        q,
        sort,
        // * Ignore fulfillmentPreference as its no longer in use.
        fulfillmentPreference,
        ...others
      } = parse(query);

      const shouldUpdateFilters =
        filter &&
        Object.keys(filter).some((appliedFilter) =>
          Object.keys(LEGACY_FILTER_MAP).includes(appliedFilter),
        );
      if (shouldUpdateFilters || fulfillmentPreference) {
        const normalizedFilterParam = shouldUpdateFilters
          ? normalizeShopFilterParam(filter as Query)
          : undefined;

        const newQueryString = stringify(
          { filter: normalizedFilterParam, q, sort, ...others },
          { arrayFormat: "brackets", indices: false },
        );
        return {
          redirect: {
            destination: `${path}${newQueryString ? `?${newQueryString}` : ""}`,
            statusCode: 302,
          },
        };
      }

      const { page } = context.query;
      const currentPage = (parseInt(page as string) || 1) - 1;
      const countryCode = getCountryCodeHeader(context.req.headers);
      const { coordinates } = await getUserLocationData(countryCode, context);

      const apiParams = {
        filter: filter as Query,
        fulfillment: fulfillment as string[],
        lat: coordinates?.latitude,
        lon: coordinates?.longitude,
        q: q as string,
        skip: currentPage * UNIFIED_SHOP_PAGE_SIZE,
        sort: sort as Query,
        take: UNIFIED_SHOP_PAGE_SIZE,
      };

      debugAPIParams = stringify(apiParams, {
        arrayFormat: "brackets",
        indices: false,
      });
      data = await getProductsV3(apiParams);
    }

    return {
      props: {
        availableFilters: data?.metadata.availableFilters || [],
        debugAPIParams,
        disableFixedHeader: true,
        env: serverConfig.env,
        shopItems: data?.data || [],
        totalCount: data?.metadata.totalCount || 0,
        unifiedShopExperienceEnabled: unifiedShopExperienceEnabled,
      },
    };
  },
);

export default ShopPage;
